import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Grid,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";
import { getEntityIconByName } from "../../Helpers/getEntityIcon";
import { entityIcons } from "../../Constants/entityIcons";

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: color.primary,
  borderRadius: 2,
  boxShadow: `0 2px 8px ${color.primary_not_present_in_design}`,
  p: 3,
  maxHeight: "80vh",
  overflowY: "auto",
  outline: "none",
};

interface UpdateEntityIconProps {
  open: boolean;
  onClose: () => void;
  selectedIconId: number;
  onSelectIcon: (iconId: number) => void;
  onIconChanged?: () => void;
}

export const UpdateEntityIcon: React.FC<UpdateEntityIconProps> = ({
  open,
  onClose,
  selectedIconId,
  onSelectIcon,
  onIconChanged,
}) => {
  const { t } = useTranslation("singleCompanyComponent");

  const [currentSelectedId, setCurrentSelectedId] =
    useState<number>(selectedIconId);

  const handleSave = () => {
    if (currentSelectedId !== selectedIconId && onIconChanged) {
      onIconChanged();
    }
    onSelectIcon(currentSelectedId);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box sx={modalStyle}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: color.primary_dark,
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography
          variant="h4"
          mb={3}
          sx={{ color: color.primary_dark, textAlign: "center" }}
        >
          {t("singleCompanyComponent.selectIcon")}
        </Typography>

        <Grid container spacing={1}>
          {entityIcons.map((iconObj, index) => {
            const iconId = index + 1;
            const isSelected = iconId === currentSelectedId;

            return (
              <Grid
                item
                xs={12 / 5}
                key={iconObj.key}
                sx={{ textAlign: "center" }}
              >
                <IconButton
                  onClick={() => setCurrentSelectedId(iconId)}
                  sx={{
                    borderRadius: "12px",
                    border: isSelected
                      ? `2px solid ${color.accent}`
                      : "2px solid transparent",
                    backgroundColor: isSelected
                      ? "rgba(255,255,255,0.1)"
                      : "transparent",
                    boxShadow: isSelected ? `0 0 6px ${color.accent}` : "none",
                    transition: "0.25s",
                  }}
                  aria-label={t(
                    `singleCompanyComponent.entityIcons.${iconObj.key}`
                  )}
                  size="large"
                >
                  {React.cloneElement(getEntityIconByName(iconObj.iconName)!, {
                    fontSize: "large",
                  })}
                </IconButton>

                <Typography
                  variant="body2"
                  color={color.primary_dark}
                  mt={0.5}
                  noWrap
                  title={t(`singleCompanyComponent.entityIcons.${iconObj.key}`)}
                >
                  {t(`singleCompanyComponent.entityIcons.${iconObj.key}`)}
                </Typography>
              </Grid>
            );
          })}
        </Grid>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              backgroundColor: color.secondary,
              color: color.white,
              "&:hover": { backgroundColor: color.secondary },
            }}
          >
            {t("singleCompanyComponent.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!currentSelectedId}
            sx={{
              backgroundColor: color.accent,
              color: "white",
              "&:hover": {
                backgroundColor: color.primary_dark,
                color: color.accent,
              },
            }}
          >
            {t("singleCompanyComponent.saveIcon")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
